export const PARENT_FOCUS_EVENT_KEY = 'parent-focus-request';
export const CHILD_CLOSE_SIGNAL_KEY = 'tabManager:childCloseSignal';
export const PARENT_WINDOW_REOPEN_SIGNAL_KEY = 'parent-window-reopen-signal';

type NullableWindow = Window | null;

export interface OpenTabEntry {
    name: string;
    timestamp: Date;
    window: Window;
}

interface ReopenSignal {
    parentTab: string;
    timestamp: number;
}

interface ParentFocusSignal {
    targetTab: string;
    timestamp: number;
}

interface ChildCloseSignal {
    childTab: null | string;
    parentTab: null | string;
    timestamp: number;
    type: 'CHILD_CLOSE_REQUEST';
}

interface ReloadPayload {
    childTab: null | string;
    elementId: string;
    parentTab: null | string;
    timestamp: number;
    type: 'REQUEST_PARENT_RELOAD';
}

export interface ParentContext {
    childTabName: null | string;
    isChildWindow: boolean;
    params: URLSearchParams;
    parentOrigin: null | string;
    parentTabName: null | string;
    parentWindow: NullableWindow;
}

export interface OpenChildResult {
    childTabName: string;
    childWindow: NullableWindow;
    openerName: string;
}

export interface SessionEntry {
    key: string;
    value: unknown;
}

export function upsertSessionStorage(entries: SessionEntry[]): void {
    for (const { key, value } of entries) {
        sessionStorage.setItem(key, JSON.stringify(value));
    }
}

export function captureParams(snapshot: any): void {
    const data = [];
    const parentTab = snapshot.queryParamMap.get('parentTab');
    const parentOrigin = snapshot.queryParamMap.get('parentOrigin');
    const childTab = snapshot.queryParamMap.get('childTab');
    if (parentTab) data.push({ key: 'parentTab', value: parentTab });
    if (parentOrigin) data.push({ key: 'parentOrigin', value: parentOrigin });
    if (childTab) data.push({ key: 'childTab', value: childTab });
    upsertSessionStorage(data);
}

export function getSessionValue<T>(key: string): T | null {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw) as T;
}

export function removeSessionKeys(keys: string[]): void {
    for (const key of keys) {
        sessionStorage.removeItem(key);
    }
}

export function recordParentWindowReopen(
    windowRef: Window,
    parentTabName: string
): void {
    try {
        const signal: ReopenSignal = {
            parentTab: parentTabName,
            timestamp: Date.now(),
        };
        windowRef.localStorage.setItem(
            PARENT_WINDOW_REOPEN_SIGNAL_KEY,
            JSON.stringify(signal)
        );
    } catch (error) {
        console.warn('Unable to record parent window reopen:', error);
    }
}

export function isParentRecentlyReopened(
    windowRef: Window,
    parentTabName: string
): boolean {
    try {
        const signalStr = windowRef.localStorage.getItem(
            PARENT_WINDOW_REOPEN_SIGNAL_KEY
        );
        if (!signalStr) {
            return false;
        }

        const signal = JSON.parse(signalStr) as ReopenSignal;
        const timeSinceReopen = Date.now() - signal.timestamp;
        const isRecent = timeSinceReopen < 2000;
        const isForSameParent = signal.parentTab === parentTabName;

        return isRecent && isForSameParent;
    } catch (error) {
        console.debug(
            'Checking parent reopen signal failed (expected if none recorded):',
            error
        );
        return false;
    }
}

export function getSafeParentWindow(
    windowRef: Window,
    parentTabName: null | string,
    url: string
): NullableWindow {
    if (!parentTabName) {
        return null;
    }

    let parentWindow: NullableWindow = null;

    try {
        parentWindow = windowRef.open(url, parentTabName);
        if (parentWindow && !parentWindow.closed) {
            return parentWindow;
        }
    } catch (error) {
        console.warn('Unable to reference existing parent window:', error);
    }

    if (isParentRecentlyReopened(windowRef, parentTabName)) {
        return null;
    }

    try {
        parentWindow = windowRef.open(url, parentTabName);
        recordParentWindowReopen(windowRef, parentTabName);
        return parentWindow;
    } catch (error) {
        console.error('Unable to open parent window:', error);
        return null;
    }
}

export function ensureWindowName(windowRef: Window): string {
    if (!windowRef.name) {
        windowRef.name = `tab_${Date.now()}_${generateSafeRandomString()}`;
    }

    return windowRef.name;
}

function generateSafeRandomString(): string {
    // 1. Use randomUUID if available (modern browsers)
    if (globalThis.crypto?.randomUUID) {
        return globalThis.crypto.randomUUID().replaceAll('-', '').slice(0, 8);
    }

    // 2. Cryptographically secure random bytes fallback (all standard environments)
    if (globalThis.crypto?.getRandomValues) {
        const bytes = new Uint8Array(4);
        globalThis.crypto.getRandomValues(bytes);
        return Array.from(bytes, (b) => b.toString(36).padStart(2, '0'))
            .join('')
            .slice(0, 8);
    }

    // 3. Simple high-precision timestamp fallback (no Math.random())
    return performance.now().toString(36).replaceAll('.', '').slice(0, 8);
}

export function getParentContext(
    windowRef: Window,
    url: string
): ParentContext {
    const params = new URL(windowRef.location.href).searchParams;
    const parentTabName =
        params.get('parentTab') || getSessionValue('parentTab');
    const parentOrigin =
        params.get('parentOrigin') || getSessionValue('parentOrigin');
    const childTabName = params.get('childTab') || getSessionValue('childTab');

    let parentWindow: NullableWindow = null;

    if (parentTabName) {
        parentWindow = getSafeParentWindow(windowRef, parentTabName, url);
    }

    parentWindow ??= windowRef.opener;

    return {
        childTabName,
        isChildWindow: Boolean(parentTabName && parentOrigin),
        params,
        parentOrigin,
        parentTabName,
        parentWindow,
    };
}

export function openChildFromCurrentUrl(windowRef: Window): OpenChildResult {
    const openerName = ensureWindowName(windowRef);
    const childTabName = `child_${Date.now()}_${generateSafeRandomString()}`;
    const url = new URL(windowRef.location.href);

    url.searchParams.set('parentTab', openerName);
    url.searchParams.set('parentOrigin', windowRef.location.origin);
    url.searchParams.set('childTab', childTabName);

    const childWindow = windowRef.open(url.toString(), childTabName);

    return {
        childTabName,
        childWindow,
        openerName,
    };
}

export function closeAndNotifyParent(
    windowRef: Window,
    context: ParentContext,
    knownParentWindow: NullableWindow,
    url: string,
    elementId: string
): void {
    const { childTabName, parentOrigin, parentTabName } = context;
    const reloadPayload: ReloadPayload = {
        childTab: childTabName || null,
        elementId,
        parentTab: parentTabName,
        timestamp: Date.now(),
        type: 'REQUEST_PARENT_RELOAD',
    };

    const notifyParent = (targetWindow: Window): void => {
        targetWindow.focus();
        targetWindow.postMessage(reloadPayload, parentOrigin || '*');
    };

    recordParentFocusSignal(windowRef, parentTabName);
    notifyParentViaMultipleChannels(
        windowRef,
        parentTabName,
        knownParentWindow,
        parentOrigin,
        notifyParent,
        url
    );
    recordChildCloseSignal(windowRef, parentTabName, childTabName);

    safeCloseCurrentWindow(windowRef, url);
}

function safeCloseCurrentWindow(windowRef: Window, fallbackUrl: string): void {
    try {
        windowRef.close();
    } catch (error) {
        console.warn('window.close() threw an error:', error);
    }

    windowRef.setTimeout(function () {
        if (windowRef.closed) {
            return;
        }

        try {
            windowRef.location.href = fallbackUrl;
        } catch (error) {
            console.error('Unable to redirect after failed close:', error);
        }
    }, 80);
}

function recordParentFocusSignal(
    windowRef: Window,
    parentTabName: null | string
): void {
    if (!parentTabName) {
        return;
    }

    try {
        const signal: ParentFocusSignal = {
            targetTab: parentTabName,
            timestamp: Date.now(),
        };
        windowRef.localStorage.setItem(
            PARENT_FOCUS_EVENT_KEY,
            JSON.stringify(signal)
        );
    } catch (error) {
        console.error('Unable to write parent focus storage signal:', error);
    }
}

function notifyParentViaMultipleChannels(
    windowRef: Window,
    parentTabName: null | string,
    knownParentWindow: NullableWindow,
    parentOrigin: null | string,
    notifyParent: (targetWindow: Window) => void,
    url: string
): void {
    if (parentTabName) {
        let parentByName = getSafeParentWindow(windowRef, parentTabName, url);
        if (
            !parentByName &&
            isParentRecentlyReopened(windowRef, parentTabName)
        ) {
            windowRef.setTimeout(function () {
                parentByName = getSafeParentWindow(
                    windowRef,
                    parentTabName,
                    url
                );
                if (parentByName && !parentByName.closed) {
                    try {
                        notifyParent(parentByName);
                    } catch (error) {
                        console.error(
                            'Unable to notify named parent window (retry):',
                            error
                        );
                    }
                }
            }, 150);
        } else if (parentByName && !parentByName.closed) {
            try {
                notifyParent(parentByName);
            } catch (error) {
                console.error('Unable to notify named parent window:', error);
            }
        }
    }

    if (knownParentWindow && !knownParentWindow.closed && parentOrigin) {
        try {
            notifyParent(knownParentWindow);
        } catch (error) {
            console.error('Unable to notify parent window:', error);
        }
    }
}

function recordChildCloseSignal(
    windowRef: Window,
    parentTabName: null | string,
    childTabName: null | string
): void {
    try {
        const signal: ChildCloseSignal = {
            childTab: childTabName || null,
            parentTab: parentTabName,
            timestamp: Date.now(),
            type: 'CHILD_CLOSE_REQUEST',
        };
        windowRef.localStorage.setItem(
            CHILD_CLOSE_SIGNAL_KEY,
            JSON.stringify(signal)
        );
    } catch (error) {
        console.error('Unable to write child close storage signal:', error);
    }
}
